import { useState, useEffect, useCallback, useRef } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import {
  deployMetricsApiRef,
  MetricsQuery,
  AggregatedMetrics,
} from '../api/DeployMetricsApi';

export interface DeployMetricsState {
  data: AggregatedMetrics | null;
  loading: boolean;
  error: Error | null;
  retrying: boolean;
  retry: () => void;
}

const MAX_RETRIES = 3;
const RETRY_DELAYS = [1000, 2000, 4000];

function isTransientError(err: unknown): boolean {
  if (err instanceof TypeError) {
    return true;
  }
  if (err instanceof Error) {
    const message = err.message || '';
    if (
      message.includes('504') ||
      message.includes('503') ||
      message.includes('Gateway Timeout') ||
      message.includes('Service Unavailable')
    ) {
      return true;
    }
  }
  if (typeof err === 'object' && err !== null) {
    const statusCode =
      (err as { statusCode?: number }).statusCode ??
      (err as { status?: number }).status;
    if (statusCode === 504 || statusCode === 503) {
      return true;
    }
  }
  return false;
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export function useDeployMetrics(
  query?: MetricsQuery,
  refreshKey?: number,
): DeployMetricsState {
  const api = useApi(deployMetricsApiRef);
  const apiRef = useRef(api);
  apiRef.current = api;

  const [data, setData] = useState<AggregatedMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [retrying, setRetrying] = useState(false);
  const cancelledRef = useRef(false);

  const fetchWithRetry = useCallback(
    async (currentQuery?: MetricsQuery) => {
      cancelledRef.current = false;
      setLoading(true);
      setError(null);
      setRetrying(false);

      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        if (cancelledRef.current) return;

        try {
          const result = await apiRef.current.getMetrics(currentQuery);
          if (!cancelledRef.current) {
            setData(result);
            setError(null);
            setRetrying(false);
            setLoading(false);
          }
          return;
        } catch (err) {
          if (cancelledRef.current) return;

          const isTransient = isTransientError(err);
          const hasRetriesLeft = attempt < MAX_RETRIES;

          if (isTransient && hasRetriesLeft) {
            setRetrying(true);
            await delay(RETRY_DELAYS[attempt]);
          } else {
            // Non-transient error or all retries exhausted
            setError(err instanceof Error ? err : new Error(String(err)));
            setRetrying(false);
            setLoading(false);
            return;
          }
        }
      }
    },
    [],
  );

  useEffect(() => {
    cancelledRef.current = false;
    fetchWithRetry(query);

    return () => {
      cancelledRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fetchWithRetry,
    query?.projectId,
    query?.startDate,
    query?.endDate,
    query?.ref,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    query?.environments?.join(','),
    query?.excludeOwner,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    query?.excludeOwners?.join(','),
    query?.includeOwner,
    refreshKey,
  ]);

  const retry = useCallback(() => {
    fetchWithRetry(query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchWithRetry, query]);

  return { data, loading, error, retrying, retry };
}
